import React from "react";
import { FaMousePointer, FaRegCalendarCheck, FaLayerGroup, FaStar, FaImage } from "react-icons/fa";

const TILE_ACCENTS = {
  indigo: { icon: "#4a5cff", bg: "#eef1ff", ring: "rgba(74,92,255,0.12)" },
  teal: { icon: "#0f8b8d", bg: "#e6f7f7", ring: "rgba(15,139,141,0.12)" },
  gold: { icon: "#b8860b", bg: "#fff6e0", ring: "rgba(184,134,11,0.12)" },
  terracotta: { icon: "#d9665b", bg: "#fdecea", ring: "rgba(217,102,91,0.12)" },
  violet: { icon: "#6d4fc4", bg: "#f1ecfb", ring: "rgba(109,79,196,0.12)" },
};

const StatTile = ({ accent, icon: Icon, label, value, subtitle }) => {
  const colours = TILE_ACCENTS[accent];
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-[20px] border border-[#e7ebf8] bg-white px-4 py-4 shadow-[0_12px_26px_rgba(15,23,42,0.05)]">
      <div
        className="flex h-11 w-11 flex-none items-center justify-center rounded-full"
        style={{ backgroundColor: colours.bg, boxShadow: `0 0 0 6px ${colours.ring}` }}
      >
        <Icon size={18} color={colours.icon} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[1.35rem] font-semibold leading-tight text-[#1f2d4d]">{value}</div>
        <div className="text-xs font-medium uppercase tracking-[0.08em] text-[#8a97b4]">{label}</div>
        {subtitle && <div className="mt-1 break-words text-xs leading-snug text-[#6b7894]">{subtitle}</div>}
      </div>
    </div>
  );
};

const SkeletonTile = () => (
  <div className="flex min-w-0 items-center gap-3 rounded-[20px] border border-[#e7ebf8] bg-white px-4 py-4 shadow-[0_12px_26px_rgba(15,23,42,0.05)]">
    <div className="h-11 w-11 flex-none animate-pulse rounded-full bg-[#eef1f8]" />
    <div className="flex-1 space-y-2">
      <div className="h-4 w-16 animate-pulse rounded bg-[#eef1f8]" />
      <div className="h-3 w-20 animate-pulse rounded bg-[#eef1f8]" />
    </div>
  </div>
);

const TsVenueStatTiles = ({ loading = false, trend = [], topContent = [], moduleBreakdown = [], bannerBreakdown = [] }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => <SkeletonTile key={i} />)}
      </div>
    );
  }

  const totalClicks = trend.reduce((sum, item) => sum + (Number(item.totalCount) || 0), 0);
  const activeDays = trend.filter((item) => Number(item.totalCount) > 0).length;
  const totalDays = trend.length;
  const topModule = moduleBreakdown[0];
  const topContentItem = topContent[0];
  const totalBannerClicks = bannerBreakdown.reduce((sum, item) => sum + (Number(item.totalCount) || 0), 0);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <StatTile
        accent="indigo"
        icon={FaMousePointer}
        label="Total Clicks"
        value={totalClicks.toLocaleString()}
        subtitle="Content taps in range"
      />
      <StatTile
        accent="teal"
        icon={FaRegCalendarCheck}
        label="Active Days"
        value={totalDays ? `${activeDays}/${totalDays}` : "0"}
        subtitle="Days with any activity"
      />
      <StatTile
        accent="gold"
        icon={FaLayerGroup}
        label="Top Module"
        value={topModule ? topModule.totalCount.toLocaleString() : "-"}
        subtitle={topModule ? topModule.module : "No module activity yet"}
      />
      <StatTile
        accent="terracotta"
        icon={FaStar}
        label="Top Content"
        value={topContentItem ? topContentItem.totalCount.toLocaleString() : "-"}
        subtitle={topContentItem ? topContentItem.name : "No content clicks yet"}
      />
      <StatTile
        accent="violet"
        icon={FaImage}
        label="Banner Clicks"
        value={totalBannerClicks.toLocaleString()}
        subtitle={bannerBreakdown.length ? `${bannerBreakdown.length} banner${bannerBreakdown.length === 1 ? "" : "s"} tracked` : "No banner activity yet"}
      />
    </div>
  );
};

export default TsVenueStatTiles;
